import { useState, useEffect } from "react";
import { useBaseAction } from "hooks/core";
import { API } from "constants/RestApi";
import { IRoleApiResponse } from "core/types/base/IRole";
import { ICustomReactSelectOption } from "core/types/elements/ICustomReactSelectOption";
import { toast } from "react-toastify";

type TProps = {
  fetchOnLoad?: boolean;
  tenantId?: string; // If provided, fetches roles for specific tenant. If not provided, fetches all roles.
};

const useRoles = ({ fetchOnLoad = true, tenantId }: TProps = {}) => {
  const { request } = useBaseAction();
  const [roles, setRoles] = useState<IRoleApiResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getRoles = async () => {
    try {
      setLoading(true);
      setError(null);

      let url = API.ADMIN_ROLES_WITH_PERMISSIONS;

      // If tenantId is provided, filter by tenantId
      if (tenantId) {
        const filter = `tenantId eq ${tenantId}`;
        url = `${API.ADMIN_ROLES_WITH_PERMISSIONS}?$filter=${encodeURIComponent(filter)}`;
        console.log("Fetching roles for tenant:", tenantId);
      } else {
        console.log("Fetching all roles (no tenant filter)");
      }

      const response = await request({
        method: "GET",
        url,
        useLoader: false, // We'll handle loading state ourselves
      });
      setRoles(response || []);
    } catch (err: any) {
      const errorMessage = err?.message || "Failed to fetch roles";
      setError(errorMessage);
      const contextMessage = tenantId
        ? `Failed to fetch roles for tenant ${tenantId}: ${errorMessage}`
        : `Failed to fetch all roles: ${errorMessage}`;
      toast.error(contextMessage);
      console.error("Error fetching roles:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (fetchOnLoad) {
      getRoles();
    }
  }, [fetchOnLoad, tenantId]);

  // Convert roles to options for react-select
  // Use role.name as value so we pass the name instead of id
  const roleOptions: ICustomReactSelectOption[] = roles.map((role) => ({
    value: role.name,
    label: role.name,
  }));

  return {
    roles,
    roleOptions,
    loading,
    error,
    getRoles,
  };
};

export default useRoles;
